import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Icon } from "@/components/Icon";

import { Button } from "@/components/ui/button";
import { FaqList } from "@/components/FaqAccordion";
import { Reveal } from "@/components/Reveal";
import { MobileStickyBar } from "@/components/MobileStickyBar";
import { AGENCY } from "@/lib/agency";
import { buildHead, orgRef } from "@/lib/seo";
import { absoluteUrl } from "@/lib/site-config";
import { getServiceArea, serviceAreas } from "@/lib/service-areas";
import { loanProducts } from "@/lib/loan-products";

const HERO_IMG =
  "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1600&q=80";

const featuredProducts = loanProducts.filter((p) => p.featured).slice(0, 8);

const PRODUCT_ICON_NAME: Record<string, string> = {
  "sba-loans": "award",
  "sba-7a-loan": "badgecheck",
  "sba-loan-for-daycare": "heart",
  "sba-loan-for-franchise": "store",
  "sba-express-loan-for-veterans": "shieldcheck",
  "working-capital-loans": "wallet",
  "business-line-of-credit": "creditcard",
  "small-business-loans": "briefcase",
  "short-term-business-loans": "zap",
  "merchant-cash-advance": "receipt",
  "unsecured-business-loans": "sparkles",
  "startup-business-loans": "lightbulb",
  "business-acquisition-loans": "handcoins",
  "accounts-receivable-financing": "filetext",
  "invoice-factoring": "receipt",
  "revenue-based-financing": "trendingup",
  "same-day-business-loans": "clock",
  "business-lenders-near-me": "mappin",
  "business-loan-broker": "compass",
  "commercial-loan-broker": "building2",
  "equipment-financing": "wrench",
  "manufacturing-equipment-financing": "factory",
  "agriculture-equipment-financing": "tractor",
  "landscaping-equipment-financing": "scissors",
  "dental-practice-loans": "stethoscope",
  "medical-practice-loans": "stethoscope",
  "restaurant-loans": "utensils",
  "trucking-business-loans": "truck",
  "commercial-construction-loan": "hammer",
  "hotel-loans": "hotel",
  "salon-business-loans": "scissors",
  "retail-business-loans": "store",
  "auto-repair-shop-financing": "car",
  "ecommerce-business-funding": "shoppingbag",
  "food-truck-financing": "coffee",
  "loan-for-gym-business": "dumbbell",
  "farm-credit-financing": "sprout",
  "women-owned-business-funding": "users",
};
const iconFor = (slug: string) => PRODUCT_ICON_NAME[slug] ?? "dollarsign";

const trustPoints = [
  { icon: "clock", t: "Decisions in 1 hour" },
  { icon: "handcoins", t: "$0 upfront fees" },
  { icon: "shieldcheck", t: "Texas-based advisors" },
];

const localAdvantages = [
  {
    icon: "compass",
    title: "Local-focused advisors",
    body: "We work with DFW owners every day. We know the documentation, licensing, and lender preferences for businesses in your area.",
  },
  {
    icon: "users",
    title: "One advisor, start to funding",
    body: "Same person from the first call through funding. No call-center queue, no re-explaining your business to a new rep every week.",
  },
  {
    icon: "handcoins",
    title: "Lender panel built for Texas",
    body: "SBA preferred lenders, regional Texas banks, equipment specialists, and revenue-based funders that already underwrite businesses in the metro.",
  },
];

const steps = [
  {
    title: "Quick intake call",
    body: "Tell us about your business, how much you need, and what it's for. Most intakes take 10-15 minutes.",
  },
  {
    title: "Package the file once",
    body: "We collect the documents lenders ask for and structure the request the way each lender underwrites it.",
  },
  {
    title: "Compare real offers",
    body: "You review actual term sheets (amount, term length, payment, fees) side by side. Nothing is binding until you sign.",
  },
  {
    title: "Fund and grow",
    body: "Funds land in your business account, often within days. Your advisor stays available for renewals and follow-on programs.",
  },
];

const qualifyBasics = [
  "Business operating in or serving the local market",
  "Typically 6+ months in business (program-dependent)",
  "Monthly revenue benchmarks vary by program",
  "Personal credit reviewed, but not the only factor",
  "U.S.-based business entity with a business bank account",
  "Industry must be eligible under the lender's guidelines",
];

export const Route = createFileRoute("/service-areas/$slug")({
  loader: ({ params }) => {
    const area = getServiceArea(params.slug);
    if (!area) throw notFound();
    return { area };
  },
  head: ({ loaderData }) => {
    const area = loaderData?.area;
    if (!area) return {};
    const title = `Business Loans in ${area.city}, ${area.state} | ${AGENCY.name}`;
    const description = `Working capital, SBA loans, lines of credit, and equipment financing for ${area.city} businesses. One Dallas advisor from intake to funding - no upfront fees.`;
    const path = `/service-areas/${area.slug}`;
    const head = buildHead({
      title,
      description,
      path,
      image: HERO_IMG,
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Service Areas", path: "/service-areas" },
        { name: `${area.city}, ${area.state}`, path },
      ],
      graph: [
        {
          "@type": "Service",
          name: `Business Loans in ${area.city}, ${area.state}`,
          provider: orgRef,
          areaServed: { "@type": "City", name: `${area.city}, ${area.state}` },
          url: absoluteUrl(path),
        },
      ],
    });
    // Preload the eager above-the-fold hero so the browser fetches the LCP
    // image immediately instead of after HTML parse/hydration.
    return {
      ...head,
      links: [
        ...head.links,
        { rel: "preload", as: "image", href: HERO_IMG, fetchPriority: "high" },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-6 py-24 text-center">
      <h1 className="font-serif text-3xl font-semibold">Area not found</h1>
      <p className="mt-4 text-muted-foreground">
        We couldn't find that service area.{" "}
        <Link to="/service-areas" className="text-accent underline">
          See all areas
        </Link>
        .
      </p>
    </div>
  ),
  errorComponent: ({ error, reset }) => (
    <div className="mx-auto max-w-2xl px-6 py-24 text-center">
      <h1 className="font-serif text-3xl font-semibold">Something went wrong</h1>
      <p className="mt-4 text-muted-foreground">{error.message}</p>
      <Button onClick={reset} className="mt-6">Try again</Button>
    </div>
  ),
  component: ServiceAreaPage,
});

function ServiceAreaPage() {
  const { area } = Route.useLoaderData();
  const cityState = `${area.city}, ${area.state}`;

  const faqs = [
    {
      q: `Do I need to be located in ${area.city} to qualify?`,
      a: `Your business should be operating in or serving the ${area.city} area, but our advisors work with owners across the DFW metro. Approval is driven by revenue, time in business, credit, and industry, not your specific ZIP code.`,
    },
    {
      q: `How fast can a ${area.city} business get funded?`,
      a: "Speed depends on the product. Working capital and lines of credit often fund in 1-5 business days. SBA programs typically take 30-60 days. We'll tell you the realistic timeline on the first call.",
    },
    {
      q: `Do you have an office in ${area.city}?`,
      a: `Our office is in Dallas at ${AGENCY.address}. We serve ${area.city} remotely by phone and secure document upload, which is how most owners prefer to handle financing. We don't claim a branch where we don't have one.`,
    },
    {
      q: "How much does it cost to apply?",
      a: "Nothing upfront. There's no charge for the intake call, document review, or shopping the file to lenders. You only pay closing costs that the lender discloses on the term sheet, and only if you decide to move forward.",
    },
    {
      q: "Will applying hurt my credit?",
      a: "The initial review uses a soft pull that doesn't affect your score. A hard pull only happens when you authorize moving forward with a specific lender on a specific offer.",
    },
  ];

  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden bg-primary text-primary-foreground">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            backgroundImage:
              "radial-gradient(ellipse at 85% 0%, oklch(0.42 0.16 250) 0%, transparent 55%), radial-gradient(ellipse at 0% 100%, oklch(0.3 0.1 260) 0%, transparent 50%)",
          }}
        />
        <div className="relative mx-auto grid max-w-7xl gap-10 px-6 py-20 md:py-24 lg:grid-cols-[1.1fr_1fr] lg:items-center lg:px-10">
          <div>
            <nav className="mb-6 text-xs text-primary-foreground/70">
              <Link to="/" className="hover:text-primary-foreground">Home</Link>
              <span className="mx-2">/</span>
              <Link to="/service-areas" className="hover:text-primary-foreground">
                Service Areas
              </Link>
              <span className="mx-2">/</span>
              <span className="text-primary-foreground">{area.city}</span>
            </nav>
            <p className="inline-flex items-center gap-2 rounded-full border border-primary-foreground/20 bg-primary-foreground/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-primary-foreground/85">
              <Icon name="mappin" color="bl" className="h-3.5 w-3.5 text-accent brightness-0 invert"  />
              Serving {cityState}
            </p>
            <h1 className="mt-6 max-w-2xl font-serif text-5xl font-semibold leading-[1.05] tracking-tight md:text-6xl">
              Business loans in {area.city}.
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-primary-foreground/85">
              {AGENCY.name} arranges working capital, SBA loans, lines of
              credit, and equipment financing for businesses in {area.city}{" "}
              and the surrounding DFW area. One advisor, one process, from
              intake to funding.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                asChild
                size="lg"
                className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90"
              >
                <a href={AGENCY.phoneHref}>
                  <Icon name="phonecall" color="bl" className="h-4 w-4 brightness-0 invert"  /> Call {AGENCY.phone}
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="rounded-full border-primary-foreground/30 bg-transparent text-primary-foreground hover:bg-primary-foreground/10"
              >
                <Link to="/apply-funding">Apply Now</Link>
              </Button>
            </div>
            <ul className="mt-8 grid max-w-xl gap-2 text-sm text-primary-foreground/90 sm:grid-cols-3">
              {trustPoints.map(({ icon: __iconName, t }) => (
                <li key={t} className="flex items-center gap-2">
                  <Icon name={__iconName} color="co" className="h-4 w-4 text-accent" />
                  {t}
                </li>
              ))}
            </ul>
          </div>
          <Reveal className="relative">
            <div className="overflow-hidden rounded-2xl border border-primary-foreground/15 shadow-2xl">
              <img
                src={HERO_IMG}
                alt={`Business district representing ${area.city}, Texas`}
                className="h-[380px] w-full object-cover"
                loading="eager"
              />
            </div>
            <div className="absolute -bottom-5 -left-5 hidden rounded-xl border border-border bg-background p-5 text-foreground shadow-lg md:block">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-accent">
                {area.city} advantage
              </p>
              <ul className="mt-3 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <Icon name="checkcircle2" color="bl" className="mt-0.5 h-4 w-4 text-accent"  />
                  Local-focused advisors
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="checkcircle2" color="bl" className="mt-0.5 h-4 w-4 text-accent"  />
                  Decisions typically within 1 hour
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="checkcircle2" color="bl" className="mt-0.5 h-4 w-4 text-accent"  />
                  Typical range $10K - $5M
                </li>
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* What financing is available */}
      <section className="border-b border-border bg-background">
        <div className="mx-auto max-w-5xl px-6 py-20 lg:px-10">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
            What's available
          </p>
          <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight md:text-4xl">
            Financing available to {area.city} businesses
          </h2>
          <div className="mt-6 space-y-5 text-base leading-relaxed text-muted-foreground">
            <p>
              {area.city} businesses have access to the full range of programs
              we arrange. That includes fast working-capital loans and revolving
              lines of credit for day-to-day cash flow, SBA 7(a) and 504 loans
              for larger expansion and real estate purchases, and equipment
              financing and invoice factoring for asset-heavy or B2B operators.
            </p>
            <p>
              Typical amounts range from roughly $10,000 on short-term working
              capital up to $5 million for SBA-backed programs. Final amounts,
              rates, and terms depend on the lender and the borrower profile.
              We'll quote realistic ranges for your file on the first call.
            </p>
          </div>
        </div>
      </section>

      {/* Loan products */}
      <section className="border-b border-border bg-secondary/40">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="max-w-2xl">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
                Loan products
              </p>
              <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight md:text-4xl">
                Loan products for {area.city} businesses
              </h2>
              <p className="mt-4 text-muted-foreground">
                Compare the most common programs we arrange. Each product page
                covers typical amounts, speed, qualification basics, and best
                fit.
              </p>
            </div>
          </div>
          <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {featuredProducts.map((p, i) => (
              <Reveal as="li" key={p.slug} delay={i * 40}>
                <Link
                  to="/dallas/$slug/$product"
                  params={{ slug: area.slug, product: p.slug }}
                  className="group flex h-full flex-col rounded-2xl border border-border bg-background p-6 transition hover:border-accent hover:shadow-sm"
                >
                  <Icon name={iconFor(p.slug)} color="co" className="h-10 w-10" />
                  <h3 className="mt-4 font-serif text-lg font-semibold leading-snug">
                    {p.name}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {p.tagline}
                  </p>
                  <dl className="mt-4 grid grid-cols-2 gap-3 border-t border-border pt-4 text-xs">
                    <div>
                      <dt className="text-muted-foreground">Amount</dt>
                      <dd className="mt-0.5 font-semibold">{p.range}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Speed</dt>
                      <dd className="mt-0.5 font-semibold">{p.speed}</dd>
                    </div>
                  </dl>
                  <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-accent group-hover:gap-2 transition-all">
                    View details <Icon name="chevronright" color="bl" className="h-4 w-4"  />
                  </span>
                </Link>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* Why local matters */}
      <section className="border-b border-border bg-background">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
          <div className="grid gap-10 lg:grid-cols-[1fr_1.1fr]">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
                Why local matters
              </p>
              <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight md:text-4xl">
                Why working with a local-focused advisor matters in {area.city}.
              </h2>
              <p className="mt-6 text-base leading-relaxed text-muted-foreground">
                National online portals treat every business the same. A {" "}
                {area.city} restaurant, a {area.city} contractor, and a {" "}
                {area.city} medical practice all face different cost
                structures, lease norms, and licensing requirements. Lenders
                price the difference. We know which lenders are
                currently active for {area.city}-area files, which ones to
                avoid for a given industry, and how to package the request so
                it doesn't stall in underwriting.
              </p>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                Coverage is remote: we serve {area.city} from our Dallas
                office, not a branch in town. Most files move from intro call
                to term sheet without an in-person meeting.
              </p>
            </div>
            <div className="grid gap-4">
              {localAdvantages.map(({ icon: __iconName, title, body }, i) => (
                <Reveal key={title} delay={i * 60}>
                  <article className="rounded-2xl border border-border bg-card p-6">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10 text-accent">
                      <Icon name={__iconName} color="co" className="h-5 w-5" />
                    </div>
                    <h3 className="mt-4 font-serif text-lg font-semibold">
                      {title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                      {body}
                    </p>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-b border-border bg-secondary/40">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
            How it works
          </p>
          <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight md:text-4xl">
            From first call to funded: four steps.
          </h2>
          <ol className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map((s, i) => (
              <Reveal as="li" key={s.title} delay={i * 60}>
                <div className="h-full rounded-2xl border border-border bg-background p-6">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-accent/10 font-serif text-sm font-semibold text-accent">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="mt-4 font-serif text-lg font-semibold">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {s.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* Who qualifies */}
      <section className="border-b border-border bg-background">
        <div className="mx-auto max-w-5xl px-6 py-20 lg:px-10">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
            Who qualifies
          </p>
          <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight md:text-4xl">
            Qualification basics for {area.city} owners
          </h2>
          <p className="mt-4 text-muted-foreground">
            Use these as a quick self-check. Final eligibility, amounts, and
            pricing depend on the lender and your specific borrower profile.
          </p>
          <ul className="mt-8 grid gap-3 sm:grid-cols-2">
            {qualifyBasics.map((q) => (
              <li
                key={q}
                className="flex items-start gap-3 rounded-xl border border-border bg-card p-4"
              >
                <Icon name="checkcircle2" color="co" className="mt-0.5 h-5 w-5 flex-none text-accent"  />
                <span className="text-sm">{q}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b border-border bg-secondary/40">
        <div className="mx-auto max-w-4xl px-6 py-20 lg:px-10">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
            FAQ
          </p>
          <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight md:text-4xl">
            {area.city} business loan questions
          </h2>
          <FaqList faqs={faqs} serif />
        </div>
      </section>

      {/* Other areas */}
      <section className="border-b border-border bg-background">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
            Other areas we serve
          </p>
          <h2 className="mt-3 font-serif text-2xl font-semibold">
            Nearby DFW cities
          </h2>
          <ul className="mt-6 flex flex-wrap gap-2">
            {serviceAreas
              .filter((a) => a.slug !== area.slug)
              .map((a) => (
                <li key={a.slug}>
                  <Link
                    to="/service-areas/$slug"
                    params={{ slug: a.slug }}
                    className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-4 py-2 text-sm transition hover:border-accent hover:text-accent"
                  >
                    <Icon name="mappin" color="bl" className="h-3.5 w-3.5"  />
                    {a.city}
                  </Link>
                </li>
              ))}
          </ul>
        </div>
      </section>

      {/* Final CTA */}
      <section id="apply" className="bg-primary text-primary-foreground">
        <div className="mx-auto max-w-5xl px-6 py-20 text-center lg:px-10">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
            Ready when you are
          </p>
          <h2 className="mt-4 font-serif text-4xl font-semibold leading-tight md:text-5xl">
            Talk to an advisor about your {area.city} business.
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-primary-foreground/85">
            One call, one advisor, no upfront fees. We'll tell you on the first
            conversation whether we can help and which programs to compare.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button
              asChild
              size="lg"
              className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90"
            >
              <a href={AGENCY.phoneHref}>
                <Icon name="phonecall" color="bl" className="h-4 w-4 brightness-0 invert"  /> Call {AGENCY.phone}
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="rounded-full border-primary-foreground/30 bg-transparent text-primary-foreground hover:bg-primary-foreground/10"
            >
              <Link to="/apply-funding">Apply Now</Link>
            </Button>
          </div>
          <p className="mt-6 text-xs text-primary-foreground/60">
            {AGENCY.hours} · Serving {cityState} from {AGENCY.address}
          </p>
        </div>
      </section>

      <MobileStickyBar applyHref="/apply-funding" />
    </>
  );
}
